import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../src/routes/config';
import { Layout } from './Layout';
import { AppContext } from '../src/App';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  Home,
  ArrowLeft,
  FileText,
  CheckCircle,
  AlertCircle,
  Clock,
  Loader2,
  BarChart3,
  Settings,
  Activity,
  Calendar,
  User,
  Server,
  Bot,
  Timer,
  Database,
  Zap,
  RefreshCw,
  TrendingUp
} from 'lucide-react';
import { apiClient } from '../src/Utils/apiClient';
import { useAuth } from './AuthContext'

export function HarvestRunLogView() {
  const { selectedHarvestRunData, setCurrentView, setSelectedHarvestRunData, sideBarItems, orgConfig } = useContext(AppContext);
  const { token } = useAuth();
  const { runId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [runData, setRunData] = useState<any>(selectedHarvestRunData || location.state?.selectedHarvestRunData);

  const Icon = sideBarItems?.find((item: any) => item.name === "Prompts")?.icon || Settings;

  const [logDetails, setLogDetails] = useState({});
  const [chartDetails, setChartDetails] = useState({
    promptName: '',
    promptVersion: '',
    model: '',
    provider: '',
    startTime: '',
    endTime: '',
    totalTime: '',
    averageProcessTime: '',
    startDate: '',
    AIApproved: '',
    sendForCorrection: '',
    ktTotalCharts: '',
    ktTotalChartsApproved: '',
    ktTotalChartsMissing: '',
    ktTotalChartsPending: ''

  })

  const fetchRunLog = async (logDetail: any) => {
    try {
      const response = await apiClient(`harvest/logs/runs/${logDetail?.display_harvest_id}`, {
        method: 'GET',
        token
      });
      const data = await response.json();
      setLogDetails(data?.data);

      if (data?.data?.ktChartsDetails) {
        setChartDetails((prev: any) => ({ ...prev, ktTotalCharts: data?.data?.ktChartsDetails?.totalCharts }));
        setChartDetails((prev: any) => ({ ...prev, ktTotalChartsApproved: data?.data?.ktChartsDetails?.updated }));
        setChartDetails((prev: any) => ({ ...prev, ktTotalChartsMissing: data?.data?.ktChartsDetails?.missing }));
        setChartDetails((prev: any) => ({ ...prev, ktTotalChartsPending: data?.data?.ktChartsDetails?.pending }));
      }

      if (data?.data?.summary) {
        setChartDetails((prev: any) => ({ ...prev, startDate: new Date(data?.data?.summary?.startedAt).toLocaleDateString('en-US') }));
        setChartDetails((prev: any) => ({ ...prev, startTime: new Date(data?.data?.summary?.startedAt).toLocaleTimeString('en-US', { hour: "2-digit", minute: "2-digit", second: "2-digit" }) }));
        setChartDetails((prev: any) => ({ ...prev, endTime: new Date(data?.data?.summary?.completedAt).toLocaleTimeString('en-US', { hour: "2-digit", minute: "2-digit", second: "2-digit" }) }));
        setChartDetails((prev: any) => ({ ...prev, totalTime: Number(data?.data?.summary?.totalRunTime) }));
        setChartDetails((prev: any) => ({ ...prev, averageProcessTime: Number(data?.data?.summary?.averageTimePerChart) }));
        setChartDetails((prev: any) => ({ ...prev, AIApproved: data?.data?.summary?.approvedCount }));
        setChartDetails((prev: any) => ({ ...prev, sendForCorrection: data?.data?.summary?.sendForCorrectionCount }));
        setChartDetails((prev: any) => ({ ...prev, promptName: data?.data?.summary?.promptName }));
        setChartDetails((prev: any) => ({ ...prev, promptVersion: data?.data?.summary?.promptVersions }));
        setChartDetails((prev: any) => ({ ...prev, model: data?.data?.summary?.aiModelName || data?.data?.summary?.aiModelType }));
        setChartDetails((prev: any) => ({ ...prev, provider: data?.data?.summary?.provider || '' }));
      }
    } catch (error: any) {
      console.error('Error fetching categories:', error.message);
    }
  };
  // Debug logging
  useEffect(() => {
    if (runData) {
      fetchRunLog(runData);
    } else if (runId) {
      // If we have runId from URL but no runData object, we assume runId is display_harvest_id
      // and fetch logs. We might miss some metadata shown in UI (like status, filename) 
      // if not returned by log API.
      fetchRunLog({ display_harvest_id: runId });
    }
  }, [runData, runId]);

  // Handle back navigation
  const handleBackToHarvest = () => {
    setSelectedHarvestRunData(undefined);
    setCurrentView('chat-harvest');
    navigate(ROUTES.chartReviewList);
  };


  // Show loading/error state if no data is available
  if (!runData && !runId && !logDetails) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-white">
          <div className="p-8">
            {/* Breadcrumb Navigation */}
            <div className="mb-6">
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink href="#" className="flex items-center gap-2">
                      <Home className="w-3 h-3" />
                      Dashboard
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink href="#" onClick={handleBackToHarvest}>
                      Chart Harvest
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>Run Log Details</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>

            {/* Error State */}
            <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
              <CardContent className="text-center py-16">
                <div className="max-w-md mx-auto">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-red-500" />
                  </div>
                  <h3 className="text-gray-900 font-medium mb-2">No Harvest Run Selected</h3>
                  <p className="text-gray-600 mb-4">
                    No harvest run data was found. Please go back to the Chart Harvest dashboard and select a completed run to view log details.
                  </p>
                  <Button onClick={handleBackToHarvest} className="bg-blue-600 hover:bg-blue-700">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Chart Harvest
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  // const runData = selectedHarvestRunData; // Replaced by state


  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'COMPLETED': { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
      'RUNNING': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Loader2 },
      'FAILED': { color: 'bg-red-100 text-red-800 border-red-200', icon: AlertCircle },
      'PENDING': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig['PENDING'];
    const IconComponent = config.icon;

    return (
      <Badge variant="outline" className={`${config.color} gap-1 text-xs`}>
        <IconComponent className={`w-3 h-3 ${status === 'In Progress' ? 'animate-spin' : ''}`} />
        {status}
      </Badge>
    );
  };

  const formatDateTime = (date?: Date) => {
    return {
      date: date?.toLocaleDateString(),
      time: date?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };
  const convertTime = (time: number) => {
    // const [hours, minutes] = time.split(':');
    const seconds = Math.floor(time / 1000);                // 159.02
    const minutes = Math.floor(seconds / 60); // 2
    const remSec = (seconds % 60);
    return `${minutes}m ${remSec}s`
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-white">
        <div className="p-4">
          {/* Breadcrumb Navigation */}
          <div className="mb-3">
            <Breadcrumb>
              <BreadcrumbList>
                {/* <BreadcrumbItem>
                  <BreadcrumbLink href="#" className="flex items-center gap-2">
                    <Home className="w-3 h-3" />
                    Dashboard
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator /> */}
                <BreadcrumbItem>
                  <BreadcrumbLink href="#" onClick={handleBackToHarvest}>
                    Chart Review
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>Run Log Details</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          {/* Header with Back Button */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleBackToHarvest}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Chart Review
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 mb-1 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text tracking-tight leading-tight">
                  Run Log Details - {runData?.harvest_file_name}
                </h1>
                <p className="text-sm text-gray-600">
                  Batch processing information and execution logs
                </p>
              </div>
            </div>
            {getStatusBadge(runData.status)}
          </div>

          {/* Batch Summary Section */}
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-4 h-4 text-blue-600" />
              <h2 className="text-base font-semibold text-gray-900">Batch Summary</h2>
            </div>
            <p className="text-sm text-gray-600 mb-2">Batch run overview and processing results</p>
            <div>
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-3">
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Batch ID</p>
                  <p className="text-sm font-bold text-gray-900 mt-1">{runData?.display_harvest_id}</p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Run Date & Time</p>
                  <p className="text-sm font-bold text-gray-900 mt-1">
                    {/* {formatDateTime(runData.runDate || runData.executedAt).date} {formatDateTime(runData.runTime || runData.executedAt).time} */}
                    {chartDetails?.startDate} {chartDetails?.startTime}
                  </p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Charts</p>
                  <p className="text-sm font-bold text-gray-900 mt-1">{runData?.total_charts}</p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs font-medium text-green-600 uppercase tracking-wide">AI Approved</p>
                  <p className="text-sm font-bold text-green-800 mt-1">{chartDetails?.AIApproved || 0}</p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs font-medium text-red-600 uppercase tracking-wide">AI Send For Correction</p>
                  <p className="text-sm font-bold text-red-800 mt-1">{chartDetails?.sendForCorrection || 0}</p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs font-medium text-yellow-600 uppercase tracking-wide">Initiated By</p>
                  <p className="text-sm font-bold text-yellow-800 mt-1">{runData.initiated_by_name}</p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{`${orgConfig?.name} Total charts`}</p>
                  <p className="text-sm font-bold text-red-800 mt-1">{chartDetails?.ktTotalCharts || 0}</p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs font-medium text-green-600 uppercase tracking-wide">{orgConfig?.labels?.updated}</p>
                  <p className="text-sm font-bold text-red-800 mt-1">{chartDetails?.ktTotalChartsApproved || 0}</p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs font-medium text-red-600 uppercase tracking-wide">{orgConfig?.labels?.noAction}</p>
                  <p className="text-sm font-bold text-red-800 mt-1">{chartDetails?.ktTotalChartsMissing || 0}</p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs font-medium text-yellow-600 uppercase tracking-wide">{orgConfig?.labels?.pending}</p>
                  <p className="text-sm font-bold text-red-800 mt-1">{chartDetails?.ktTotalChartsPending || 0}</p>
                </div>
              </div>

              {/* Enhanced status information */}
              {runData.status === 'Failed' && runData.failureReason && (
                <div className="mt-3 p-3 bg-white border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <p className="text-sm font-medium text-red-800">Failure Details</p>
                  </div>
                  <p className="text-xs text-red-700">{runData.failureReason}</p>
                </div>
              )}

              {runData.status === 'In Progress' && (
                <div className="mt-3 p-3 bg-white border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                    <p className="text-sm font-medium text-blue-800">Processing Status</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-blue-600">Charts Reviewed:</span>
                      <span className="font-medium text-blue-800">{runData.chartsReviewed}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-blue-600">Charts Pending:</span>
                      <span className="font-medium text-blue-800">{runData.chartsToBeReviewed}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <Separator className="my-3" />

          {/* Prompt & Model Details Section */}
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-2">
              <Icon className="w-4 h-4 text-purple-600" />
              <h2 className="text-base font-semibold text-gray-900">Prompt & Model Configuration</h2>
            </div>
            <p className="text-sm text-gray-600 mb-2">AI settings used for this batch run</p>
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">Prompt Name</p>
                  <p className="text-sm font-bold text-purple-900 mt-1">{chartDetails?.promptName || 'Healthcare Audit Prompt v3'}</p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">Prompt Version</p>
                  <p className="text-sm font-bold text-purple-900 mt-1">{chartDetails?.promptVersion || 'v1.5'}</p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">AI Model</p>
                  <p className="text-sm font-bold text-blue-900 mt-1">{chartDetails?.model || 'Claude-3.5-Sonnet'}</p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Provider</p>
                  <p className="text-sm font-bold text-blue-900 mt-1">{chartDetails?.provider || '—'}</p>
                </div>
              </div>
            </div>
          </div>

          <Separator className="my-3" />

          {/* Performance Metrics Section */}
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <h2 className="text-base font-semibold text-gray-900">Performance Metrics</h2>
            </div>
            <p className="text-sm text-gray-600 mb-2">Timing and performance statistics</p>
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-white p-3 rounded-lg">
                  <div className="flex items-center gap-1 mb-1">
                    <Timer className="w-3 h-3 text-green-600" />
                    <p className="text-xs font-medium text-green-600 uppercase tracking-wide">Start Time</p>
                  </div>
                  <p className="text-sm font-bold text-green-900">
                    {/* {runData.startTime ? formatDateTime(runData.startTime).time : 'N/A'} */}
                    {chartDetails?.startTime ? chartDetails?.startTime : 'N/A'}
                  </p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <div className="flex items-center gap-1 mb-1">
                    <CheckCircle className="w-3 h-3 text-green-600" />
                    <p className="text-xs font-medium text-green-600 uppercase tracking-wide">End Time</p>
                  </div>
                  <p className="text-sm font-bold text-green-900">
                    {/* {runData.endTime ? formatDateTime(runData.endTime).time : 'N/A'} */}
                    {chartDetails?.endTime ? chartDetails?.endTime : 'N/A'}
                  </p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <div className="flex items-center gap-1 mb-1">
                    <Zap className="w-3 h-3 text-blue-600" />
                    <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Avg Process Time</p>
                  </div>
                  <p className="text-sm font-bold text-blue-900">
                    {chartDetails?.averageProcessTime ? convertTime(chartDetails?.averageProcessTime) : 'N/A'}
                  </p>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <div className="flex items-center gap-1 mb-1">
                    <Database className="w-3 h-3 text-blue-600" />
                    <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Total Process Time</p>
                  </div>
                  <p className="text-sm font-bold text-blue-900">
                    {/* {runData.totalTimeSeconds ? `${Math.floor(runData.totalTimeSeconds / 60)}m ${runData.totalTimeSeconds % 60}s` : 'N/A'} */}
                    {chartDetails?.totalTime ? convertTime(chartDetails?.totalTime) : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <Separator className="my-3" />

          {/* Execution Log Section */}
          {runData.errorLog && (
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-red-600" />
                <h2 className="text-base font-semibold text-gray-900">Execution Log</h2>
              </div>
              <p className="text-sm text-gray-600 mb-2">Detailed log with timestamps and status messages</p>
              <div>
                <div className="bg-gray-900 rounded-lg p-3 overflow-x-auto max-h-40 overflow-y-auto">
                  <pre className="text-xs text-gray-100 whitespace-pre-wrap font-mono">
                    {runData.errorLog}
                  </pre>
                </div>
              </div>
            </div>
          )}

          {/* Success Log for completed runs */}
          {/* {runData.status === 'Completed' && !runData.errorLog && ( */}
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-green-600" />
              <h2 className="text-base font-semibold text-gray-900">Execution Log</h2>
            </div>
            <p className="text-sm text-gray-600 mb-2">Successful execution log with processing details</p>
            <div>
              <div className="bg-gray-900 rounded-lg p-3 overflow-x-auto max-h-[14rem] overflow-y-auto">
                <pre className="text-xs text-gray-100 whitespace-pre-wrap font-mono">
                  {/* Example log entries - replace with actual log data */}
                  {logDetails?.logs?.length > 0 ? (
                    logDetails?.logs?.map((log: any, index: number) => (
                      <div key={index}>{"[" + log?.created_at + "]" + " " + log?.level + ":" + " " + log?.message}</div>
                    ))
                  ) : (
                    <div className="text-gray-500">No logs available for this run.</div>
                  )}
                  {/* {`[${new Date().toISOString()}] INFO: Harvest run ${runData.batchId || runData.id} initiated
[${new Date().toISOString()}] INFO: Connecting to EMR system...
[${new Date().toISOString()}] INFO: EMR connection established successfully
[${new Date().toISOString()}] INFO: Starting chart extraction process
[${new Date().toISOString()}] INFO: Extracted ${runData.chartsInBatch || runData.totalCharts} charts from EMR system
[${new Date().toISOString()}] INFO: Initializing AI evaluation pipeline
[${new Date().toISOString()}] INFO: AI model ${runData.modal || 'Claude-3.5-Sonnet'} loaded successfully
[${new Date().toISOString()}] INFO: Processing charts with prompt ${runData.promptName || 'Healthcare Audit Prompt'} ${runData.promptVersion || 'v1.5'}
[${new Date().toISOString()}] INFO: Chart evaluation completed
[${new Date().toISOString()}] INFO: Results: ${runData.aiApprovedCharts || runData.chartsAiApproved} approved, ${runData.chartsToBeReviewed || runData.chartsNeedingReview} need review, ${runData.aiSendForCorrectionCharts || runData.chartsAiRejected || 0} rejected
[${new Date().toISOString()}] INFO: Saving results to database
[${new Date().toISOString()}] INFO: Database update completed successfully
[${new Date().toISOString()}] INFO: Harvest run ${runData.batchId || runData.id} completed successfully
[${new Date().toISOString()}] INFO: Total processing time: ${runData.totalTimeSeconds ? `${Math.floor(runData.totalTimeSeconds / 60)}m ${runData.totalTimeSeconds % 60}s` : 'N/A'}
[${new Date().toISOString()}] INFO: Average per-chart processing time: ${runData.averageProcessTime ? `${runData.averageProcessTime}s` : 'N/A'}`} */}
                </pre>
              </div>
            </div>
          </div>
          {/* )
          } */}
        </div>
      </div>
    </Layout>
  );
}